import { useCallback, useEffect, useRef, useState } from "react";
import { Sun, Moon } from "lucide-react";
import { toast } from "sonner";

/**
 * Toggle "Écran toujours allumé" — utilise l'API Wake Lock du navigateur.
 * Le bouton visible allume/éteint le wake lock. Il se ré-acquiert automatiquement
 * quand l'onglet redevient visible (Wake Lock est libéré au blur par le navigateur).
 *
 * Props :
 *   - color : "green" (Nold Food), "orange" (BentSkin Go) — pilote la couleur active
 *   - compact : bouton plus court (icône seule + label sur hover)
 */
export default function KeepScreenOnToggle({ color = "green", compact = false }) {
  const [on, setOn] = useState(false);
  const [supported, setSupported] = useState(true);
  const wlRef = useRef(null);

  useEffect(() => {
    setSupported("wakeLock" in navigator);
  }, []);

  const acquire = useCallback(async () => {
    try {
      if (!("wakeLock" in navigator)) return null;
      const wl = await navigator.wakeLock.request("screen");
      wlRef.current = wl;
      wl.addEventListener("release", () => { wlRef.current = null; });
      return wl;
    } catch {
      return null;
    }
  }, []);

  const release = useCallback(async () => {
    try { await wlRef.current?.release?.(); } catch { /* ignore */ }
    wlRef.current = null;
  }, []);

  const toggle = async () => {
    if (!supported) {
      toast.error("Votre navigateur ne supporte pas le maintien d'écran allumé. Utilisez Chrome/Safari récents.");
      return;
    }
    if (on) {
      await release();
      setOn(false);
      toast.info("Écran redevient normal (peut s'éteindre)");
    } else {
      const wl = await acquire();
      if (wl) {
        setOn(true);
        toast.success("Écran maintenant maintenu allumé 🔆");
      } else {
        toast.error("Impossible d'activer — vérifiez que l'onglet est bien visible.");
      }
    }
  };

  // Ré-acquiert quand l'onglet redevient visible (Wake Lock libéré au blur).
  useEffect(() => {
    const onVis = async () => {
      if (on && document.visibilityState === "visible" && !wlRef.current) {
        await acquire();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [on, acquire]);

  // Libère au démontage
  useEffect(() => () => { release(); }, [release]);

  const activeBg = color === "orange" ? "bg-[#F26419]" : "bg-[#059669]";
  const activeHover = color === "orange" ? "hover:bg-[#D94D0E]" : "hover:bg-[#047857]";
  const inactiveBorder = color === "orange" ? "border-[#F26419]/40 text-[#F26419]" : "border-[#059669]/40 text-[#059669]";
  const inactiveHover = color === "orange" ? "hover:bg-[#FFF3E6]" : "hover:bg-[#F0FDF4]";

  return (
    <button
      onClick={toggle}
      data-testid="keep-screen-on-toggle"
      title={on ? "Écran maintenu allumé — cliquez pour désactiver" : "Cliquez pour maintenir l'écran allumé"}
      className={`inline-flex items-center gap-2 h-10 ${compact ? "px-3" : "px-4"} rounded-full font-bold text-xs transition-all ${
        on
          ? `${activeBg} ${activeHover} text-white shadow-md`
          : `bg-white border-2 ${inactiveBorder} ${inactiveHover}`
      }`}
    >
      {on ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      {!compact && <span>{on ? "Écran allumé" : "Écran normal"}</span>}
    </button>
  );
}
